import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import ranking from './trackFilterRanking.json';
import { CULL_POLICIES, loadDayOrders, sydneyDate } from './trackFilterCull';

const venues: any[] = (ranking as any).venues;
const byKey: Record<string, any> = Object.fromEntries(venues.map(v => [v.key, v]));
const position: Record<string, number> = Object.fromEntries(venues.map((v, i) => [v.key, i + 1]));

// Read-only list of the venue ranking used by the Auto track cull.
const TrackVenueRanking: React.FC = () => {
  const today = sydneyDate(new Date().toISOString());
  const [order, setOrder] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    loadDayOrders([today], API_BASE_URL).then(o => alive && setOrder(o[today] || [])).catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [today]);
  const buckets = Object.entries(CULL_POLICIES);
  return (
    <section className="sr-card" aria-labelledby="track-venue-ranking-title" style={{ marginTop: 20, padding: 16 }}>
      <h2 id="track-venue-ranking-title">Track list used by “Cull by best ROI track”</h2>
      <p className="rc-note">
        Venues are ranked by historical winner count, not track ROI: the number of races each venue produced where
        Core rank 1, 2 or 3 won, 2020–2022 ({(ranking as any).trainingRaces.toLocaleString()} archived races).
        Each day only the venues racing that day are ranked. The Auto cull keeps a bet only when its venue is inside
        its bucket’s tested top 1, 2 or 3 for that day.
      </p>
      <h3>Today’s order · {today}</h3>
      {error && <p role="status">Today’s meeting list is unavailable: {error}</p>}
      {!error && !order && <p>Loading today’s meetings…</p>}
      {order && !order.length && <p>No ranked Australian meetings today.</p>}
      {order && !!order.length && (
        <ol>{order.map((k, i) => (
          <li key={k}><strong>{byKey[k]?.track || k}</strong> · {byKey[k]?.total_wins} winners
            {i < 3 && <small> · kept by top-{i + 1 === 1 ? '1, 2 and 3' : i + 1 === 2 ? '2 and 3' : '3'} filters</small>}</li>
        ))}</ol>
      )}
      <h3>Bucket filters</h3>
      <ul>{buckets.map(([id, b]) => <li key={id}>{b.label}: top {b.tracks} venue{b.tracks === 1 ? '' : 's'} per day</li>)}</ul>
      <details style={{ marginTop: 12 }}>
        <summary>Full historical venue ranking ({venues.length} venues)</summary>
        <div style={{ overflowX: 'auto' }}>
          <table role="table" className="sr-table mobile-card-table" aria-label="Historical venue ranking">
            <thead role="rowgroup"><tr role="row">
              <th role="columnheader">#</th><th role="columnheader">Venue</th><th role="columnheader">Winners (R1+R2+R3)</th>
              <th role="columnheader">R1</th><th role="columnheader">R2</th><th role="columnheader">R3</th>
              <th role="columnheader">Races</th><th role="columnheader">Top-3 hit rate</th>
            </tr></thead>
            <tbody role="rowgroup">{venues.map(v => (
              <tr role="row" key={v.key}>
                <td role="cell" data-label="#" className="sr-num">{position[v.key]}</td>
                <td role="cell" data-label="Venue">{v.track}</td>
                <td role="cell" data-label="Winners" className="sr-num">{v.total_wins}</td>
                <td role="cell" data-label="R1" className="sr-num">{v.wins?.[0]}</td>
                <td role="cell" data-label="R2" className="sr-num">{v.wins?.[1]}</td>
                <td role="cell" data-label="R3" className="sr-num">{v.wins?.[2]}</td>
                <td role="cell" data-label="Races" className="sr-num">{v.races}</td>
                <td role="cell" data-label="Top-3 hit rate" className="sr-num">{Number(v.top3_sr).toFixed(1)}%</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="rc-note">Busy venues rank higher partly because they hold more races. Venue groups combine course variants
          (Sandown, Morphettville/Parks, Newcastle/Beaumont, Pinjarra/Scarpside, Wagga/Riverside).</p>
      </details>
    </section>
  );
};

export default TrackVenueRanking;
